/**
 * Pop-Out Panel
 * 
 * Transient panel that appears to the right of the Activity Rail.
 * Per spec:
 * - Width: 280-320px (responsive)
 * - Height: Full viewport minus top bar
 * - Position: Absolute/fixed, anchored to rail
 * - Z-index: Above workspace, below modals
 * - Slight slide-in animation (≤150ms)
 * - Closes on click outside or Escape key
 */
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface PopOutPanelProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
}

export const PopOutPanel: React.FC<PopOutPanelProps> = ({
    isOpen,
    onClose,
    title,
    children,
}) => {
    const panelRef = useRef<HTMLDivElement>(null);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
                // Check if click is on the activity rail (don't close if switching panels)
                const target = event.target as HTMLElement;
                if (target.closest('[data-activity-rail]')) {
                    return;
                }
                onClose();
            }
        };

        if (isOpen) {
            // Delay adding listener to avoid immediate close
            const timer = setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 0);
            return () => {
                clearTimeout(timer);
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen, onClose]);

    // Handle Escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    ref={panelRef}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -20, opacity: 0 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className="
                        absolute left-14 top-0 bottom-0
                        w-80 bg-[#181825] border-r border-white/[0.06]
                        shadow-xl z-40 flex flex-col
                    "
                >
                    {/* Panel Header */}
                    <div className="px-4 py-3 border-b border-white/[0.06]">
                        <h2 className="text-sm font-medium text-gray-100">{title}</h2>
                    </div>

                    {/* Panel Content */}
                    <div className="flex-1 overflow-y-auto">
                        {children}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PopOutPanel;
